import { getApiClient } from "@src/api";
import { getShareKey } from "@src/auth/auth-utils";
import { parseChatDocumentId } from "@src/components/collaboration/document-id";
import { graphql } from "@src/gql";
import type { ChatDetailsDocumentFilterInput } from "@src/gql/graphql";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { chatAggregateId } from "../document-refs";

export const chatQueryKeys = {
	all: ["chats"] as const,
	myChats: (where?: unknown, shareKey?: string | null) =>
		shareKey
			? (["chats", "myChats", where, shareKey] as const)
			: (["chats", "myChats", where] as const),
	myChat: (chatId: string | null, shareKey?: string | null) => {
		const normalized = chatId ? chatAggregateId(chatId) : null;
		return shareKey
			? (["chats", "myChat", normalized, shareKey] as const)
			: (["chats", "myChat", normalized] as const);
	},
	messages: (chatId: string | null, shareKey?: string | null) => {
		const normalized = chatId ? chatAggregateId(chatId) : null;
		return shareKey
			? (["chats", "messages", normalized, shareKey] as const)
			: (["chats", "messages", normalized] as const);
	},
};

export const getChatMessagesQuery = graphql(/* GraphQL */ `
	query GetChatMessages($chatId: String!, $shareKey: String) {
		chats(shareKey: $shareKey, first: 1, where: { id: { eq: $chatId } }) {
			nodes {
				messages(order: [{ orderIndex: ASC }]) {
					nodes {
						id
						chatId
						role
						type
						content
						model
						rawJson
						createdAt
						orderIndex
					}
				}
			}
		}
	}
`);

export const getMyChatsQuery = graphql(/* GraphQL */ `
	query GetMyChats($where: ChatDetailsDocumentFilterInput, $shareKey: String) {
		chats(shareKey: $shareKey, where: $where, order: [{ createdAt: DESC }]) {
			nodes {
				id
				name
				status
				ownerId
				ownerEmail
				isPublished
				shareKey
				shareKeyPermission
				collaboration {
					isShared
					collaborators {
						userId
						permission
						userEmail
					}
				}
				attachedEntityIds
				attachedEntities {
					entityTypedId
					anchorId
				}
				messageCount
				metadata {
					key
					value
				}
				createdAt
				updatedAt
			}
		}
	}
`);

export const fetchChatMessagesQueryOptions = (
	chatId: string | null | undefined,
) => {
	const shareKey = getShareKey();
	return queryOptions({
		queryKey: chatQueryKeys.messages(chatId ?? null, shareKey),
		queryFn: async () => {
			if (!chatId) {
				return [];
			}
			const client = getApiClient();
			const normalizedChatId = chatAggregateId(chatId);
			const result = await client.query({
				query: getChatMessagesQuery,
				variables: {
					chatId: normalizedChatId,
					...(shareKey ? { shareKey } : {}),
				},
			});
			return result.data?.chats?.nodes?.[0]?.messages?.nodes ?? [];
		},
		enabled: Boolean(chatId),
	});
};

export const fetchMyChatsQueryOptions = (
	where?: ChatDetailsDocumentFilterInput,
	options?: { enabled?: boolean },
) => {
	const shareKey = getShareKey();
	return queryOptions({
		queryKey: chatQueryKeys.myChats(where, shareKey),
		queryFn: async () => {
			const client = getApiClient();
			const result = await client.query({
				query: getMyChatsQuery,
				variables: {
					where,
					...(shareKey ? { shareKey } : {}),
				},
			});
			return result.data?.chats?.nodes ?? [];
		},
		staleTime: 30_000,
		enabled: options?.enabled ?? true,
	});
};

export function chatIdWhere(chatId: string): ChatDetailsDocumentFilterInput {
	const doc = parseChatDocumentId(chatId);
	return {
		or: [{ id: { eq: doc.toIdString() } }, { id: { eq: doc.toString() } }],
	};
}

export function useChatMessages(chatId: string | null | undefined) {
	return useQuery(fetchChatMessagesQueryOptions(chatId));
}

export function useMyChats(
	where?: ChatDetailsDocumentFilterInput,
	options?: { enabled?: boolean },
) {
	return useQuery(fetchMyChatsQueryOptions(where, options));
}

export const fetchMyChatQueryOptions = (
	chatId: string | null | undefined,
	options?: { enabled?: boolean },
) => {
	const rawChatId = chatId ? parseChatDocumentId(chatId).toIdString() : null;
	const shareKey = getShareKey();
	return queryOptions({
		queryKey: chatQueryKeys.myChat(rawChatId, shareKey),
		queryFn: async () => {
			if (!rawChatId) {
				return null;
			}
			const client = getApiClient();
			const result = await client.query({
				query: getMyChatsQuery,
				variables: {
					where: { id: { eq: rawChatId } },
					...(shareKey ? { shareKey } : {}),
				},
			});
			return result.data?.chats?.nodes?.[0] ?? null;
		},
		enabled: (options?.enabled ?? true) && Boolean(rawChatId),
	});
};

export function useMyChat(
	chatId: string | null | undefined,
	options?: { enabled?: boolean },
) {
	return useQuery(fetchMyChatQueryOptions(chatId, options));
}
